/**
 * <s-personalization> — engraving fields inside the product form.
 *
 * Rendered by snippets/personalization.liquid inside the 'product-form' partial, so it
 * is re-created on every variant change. Values (including chosen files) live in a
 * per-product store and are restored on reconnect, so customers never lose what they typed.
 *
 * Responsibilities:
 *  - Validation of [data-pz-required] fields before add-to-cart (blocks s-product-form).
 *  - Names: one required field per unit, following the quantity input.
 *  - Letters: quantity = non-space characters, size range per variant, language-filtered font list.
 *  - Keychain: side visibility, second-side add-on line, photo positioning on the shape mask.
 *  - Submission with files or the add-on: posts multipart to /cart/add.js (Standard Actions
 *    cannot carry files), then refreshes the cart drawer. Everything else flows through
 *    <s-product-form> unchanged.
 */

const STORE = new Map();

function storeFor(productId) {
  if (!STORE.has(productId)) STORE.set(productId, new Map());
  return STORE.get(productId);
}

function fieldKey(el) {
  if (el.type === 'radio') return `${el.name}::radio`;
  return el.id || el.name || el.dataset.pzKey || '';
}

function countCharacters(text) {
  if (!text) return 0;
  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    const segmenter = new Intl.Segmenter('he', { granularity: 'grapheme' });
    return Array.from(segmenter.segment(text)).filter((s) => !/\s/u.test(s.segment)).length;
  }
  return Array.from(text).filter((c) => !/\s/u.test(c)).length;
}

class SPersonalization extends HTMLElement {
  #ac;
  #store;
  #form;
  #editor = null;

  connectedCallback() {
    this.#form = this.closest('form');
    if (!this.#form) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#store = storeFor(this.dataset.productId || 'default');

    if (this.querySelector('[data-pz-file]')) this.#form.enctype = 'multipart/form-data';

    this.#restore();

    this.addEventListener('input', (e) => this.#save(e.target), opts);
    this.addEventListener('change', (e) => this.#save(e.target), opts);

    this.#setupFiles(opts);
    this.#setupNames(opts);
    this.#setupLetters(opts);
    this.#setupKeychain(opts);

    // At-target listener on the form runs before <s-product-form>'s bubbling listener.
    this.#form.addEventListener('submit', (e) => this.#onSubmit(e), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /* ---------- persistence ---------- */

  #save(el) {
    if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) return;
    if (!el.matches('[data-pz-persist], [data-pz-file], [data-pz-name]')) return;
    const key = fieldKey(el);
    if (!key) return;
    if (el.type === 'file') this.#store.set(key, Array.from(el.files || []));
    else if (el.type === 'radio') { if (el.checked) this.#store.set(key, el.value); }
    else if (el.type === 'checkbox') this.#store.set(key, el.checked);
    else this.#store.set(key, el.value);
  }

  #restore() {
    this.querySelectorAll('[data-pz-persist], [data-pz-file]').forEach((el) => {
      const key = fieldKey(el);
      if (!this.#store.has(key)) return;
      const value = this.#store.get(key);
      if (el.type === 'file') {
        if (!value?.length || typeof DataTransfer === 'undefined') return;
        const dt = new DataTransfer();
        value.forEach((f) => dt.items.add(f));
        el.files = dt.files;
      } else if (el.type === 'radio') el.checked = el.value === value;
      else if (el.type === 'checkbox') el.checked = !!value;
      else el.value = value;
    });
  }

  /* ---------- files ---------- */

  #setupFiles(opts) {
    this.querySelectorAll('[data-pz-file]').forEach((input) => {
      const label = input.closest('[data-pz-upload], [data-pz-side1-image]')?.querySelector('[data-pz-file-name]');
      const update = () => {
        if (!label) return;
        const n = input.files?.length || 0;
        label.textContent = n === 0 ? label.dataset.none : n === 1 ? input.files[0].name : label.dataset.count.replace('__N__', n);
        if (n) this.#clearError(input);
      };
      input.addEventListener('change', update, opts);
      update();
    });
  }

  /* ---------- names per unit ---------- */

  #quantityInput() {
    return this.#form.querySelector('input[name="quantity"]');
  }

  #setupNames(opts) {
    const wrap = this.querySelector('[data-pz-names]');
    if (!wrap) return;
    const list = wrap.querySelector('[data-pz-names-list]');
    const render = () => {
      const q = Math.min(Math.max(parseInt(this.#quantityInput()?.value, 10) || 1, 1), 100);
      const rows = list.children.length;
      for (let i = rows + 1; i <= q; i++) {
        const id = `${this.dataset.productId}-name-${i}`;
        const row = document.createElement('div');
        row.className = 'grid gap-1';
        row.innerHTML = `<div class="flex items-center gap-3"><label for="${id}" class="w-16 shrink-0 text-sm font-medium text-on-surface"></label><input id="${id}" type="text" maxlength="30" autocomplete="off" data-pz-required data-pz-name class="${wrap.dataset.fieldClass}"></div><p class="text-sm text-sale" data-pz-error hidden></p>`;
        row.querySelector('label').textContent = wrap.dataset.label.replace('__N__', i);
        const input = row.querySelector('input');
        input.name = `properties[שם ${i}]`;
        input.placeholder = wrap.dataset.placeholder;
        input.value = this.#store.get(id) || '';
        list.appendChild(row);
      }
      while (list.children.length > q) list.lastElementChild.remove();
    };
    render();
    this.#form.addEventListener('s-quantity:change', render, opts);
    this.#form.addEventListener('input', (e) => { if (e.target === this.#quantityInput()) render(); }, opts);
    this.#form.addEventListener('change', (e) => { if (e.target === this.#quantityInput()) render(); }, opts);
  }

  /* ---------- letters & numbers ---------- */

  #variantId() {
    return String(this.#form.elements.namedItem('id')?.value || '');
  }

  #setupLetters(opts) {
    const wrap = this.querySelector('[data-pz-letters]');
    if (!wrap) return;
    let data = {};
    try { data = JSON.parse(this.querySelector('[data-pz-letters-data]')?.textContent || '{}'); } catch (_) { data = {}; }
    const info = data[this.#variantId()] || { min: 1, max: 30, price: 0 };

    const text = wrap.querySelector('[data-pz-letters-text]');
    const countEl = wrap.querySelector('[data-pz-letters-count]');
    const totalEl = wrap.querySelector('[data-pz-letters-total]');
    const size = wrap.querySelector('[data-pz-letters-size]');
    const help = wrap.querySelector('[data-pz-letters-help]');
    const qtyInput = this.#quantityInput();
    const qtyWrap = this.#form.querySelector('[data-quantity-wrapper]');
    if (qtyWrap) qtyWrap.hidden = true;

    const money = new Intl.NumberFormat(document.documentElement.lang || 'he-IL', { style: 'currency', currency: totalEl?.dataset.currency || 'ILS' });

    const updateCount = () => {
      const n = countCharacters(text.value);
      countEl.textContent = String(n);
      totalEl.textContent = money.format((n * info.price) / 100);
      if (qtyInput) qtyInput.value = String(Math.max(n, 1));
    };

    size.min = String(info.min);
    size.max = String(info.max);
    help.textContent = wrap.dataset.range.replace('__MIN__', info.min).replace('__MAX__', info.max);
    if (size.value) {
      const v = Number(size.value);
      if (!Number.isFinite(v) || v < info.min || v > info.max) { size.value = ''; this.#save(size); }
    }
    size.dataset.pzRangeError = wrap.dataset.rangeError.replace('__MIN__', info.min).replace('__MAX__', info.max);

    // Fonts follow the chosen language; the hidden property carries the final value.
    const lang = wrap.querySelector('[data-pz-letters-lang]');
    const font = wrap.querySelector('[data-pz-font-select]');
    const otherWrap = wrap.querySelector('[data-pz-font-other]');
    const otherInput = wrap.querySelector('[data-pz-font-other-input]');
    const fontValue = wrap.querySelector('[data-pz-font-value]');
    const syncFonts = () => {
      const code = lang.value === 'English' ? 'en' : 'he';
      font.querySelectorAll('option[data-lang]').forEach((o) => { const off = o.dataset.lang !== code; o.hidden = off; o.disabled = off; });
      if (font.selectedOptions[0]?.disabled) { font.value = font.querySelector(`option[data-lang="${code}"]`).value; this.#save(font); }
      const other = font.value === '__other__';
      otherWrap.hidden = !other;
      fontValue.value = other ? otherInput.value.trim() : font.value;
    };

    text.addEventListener('input', updateCount, opts);
    lang.addEventListener('change', syncFonts, opts);
    font.addEventListener('change', syncFonts, opts);
    otherInput.addEventListener('input', syncFonts, opts);
    updateCount();
    syncFonts();
  }

  /* ---------- keychain ---------- */

  #twoSides() {
    return this.querySelector('[data-pz-sides]:checked')?.value === 'שני צדדים';
  }

  #imageMode() {
    return this.querySelector('[data-pz-side1-type]:checked')?.value === 'image';
  }

  #setupKeychain(opts) {
    const wrap = this.querySelector('[data-pz-keychain]');
    if (!wrap) return;
    const side2 = wrap.querySelector('[data-pz-side="2"]');
    const textBox = wrap.querySelector('[data-pz-side1-text]');
    const imageBox = wrap.querySelector('[data-pz-side1-image]');

    const sync = () => {
      const two = this.#twoSides();
      side2.hidden = !two;
      side2.querySelectorAll('input').forEach((i) => { i.disabled = !two; });
      if (imageBox) {
        const img = this.#imageMode();
        textBox.hidden = img;
        textBox.querySelectorAll('input').forEach((i) => { i.disabled = img; });
        imageBox.hidden = !img;
        imageBox.querySelectorAll('input').forEach((i) => { i.disabled = !img; });
        if (img) this.#editor ??= new ShapeEditor(imageBox, this, opts);
      }
    };
    wrap.addEventListener('change', (e) => { if (e.target.matches('[data-pz-sides], [data-pz-side1-type]')) sync(); }, opts);
    sync();
    if (imageBox && !this.#editor) this.#editor = new ShapeEditor(imageBox, this, opts);
  }

  variantOptions() {
    try {
      const map = JSON.parse(this.querySelector('[data-pz-variant-options]')?.textContent || '{}');
      return map[this.#variantId()] || [];
    } catch (_) { return []; }
  }

  get store() { return this.#store; }

  /* ---------- validation ---------- */

  #setError(el, message) {
    const box = el.closest('.grid')?.querySelector(':scope > [data-pz-error]') || el.closest('[data-pz-upload], [data-pz-side1-image]')?.querySelector('[data-pz-error]');
    el.setAttribute('aria-invalid', 'true');
    if (box) { box.textContent = message; box.hidden = false; if (box.id === '') box.id = `${el.id || 'pz'}-error`; el.setAttribute('aria-describedby', box.id); }
  }

  #clearError(el) {
    el.removeAttribute('aria-invalid');
    const box = el.closest('.grid')?.querySelector(':scope > [data-pz-error]') || el.closest('[data-pz-upload], [data-pz-side1-image]')?.querySelector('[data-pz-error]');
    if (box) { box.hidden = true; box.textContent = ''; }
  }

  #validate() {
    let first = null;
    const fail = (el, msg) => { this.#setError(el, msg); first ??= el; };

    this.querySelectorAll('[data-pz-required]').forEach((el) => {
      if (el.disabled || el.closest('[hidden]')) return;
      this.#clearError(el);
      if (el.type === 'file') {
        if (!el.files?.length) fail(el, this.dataset.errorUpload);
        return;
      }
      const value = el.value.trim();
      if (!value) return fail(el, this.dataset.errorRequired);
      if (el.matches('[data-pz-letters-size]')) {
        const v = Number(value);
        if (!Number.isFinite(v) || v < Number(el.min) || v > Number(el.max)) fail(el, el.dataset.pzRangeError);
      }
    });

    if (this.#editor && this.#imageMode()) {
      const file = this.#editor.fileInput;
      this.#clearError(file);
      if (!file.files?.length) fail(file, this.dataset.errorUpload);
      else if (!this.#editor.locked) fail(file, this.dataset.errorLock);
    }

    const summary = this.querySelector('[data-pz-summary]');
    if (summary) { summary.hidden = !first; summary.textContent = first ? this.dataset.errorSummary : ''; }
    if (first) first.focus({ preventScroll: false });
    return !first;
  }

  /* ---------- submission ---------- */

  #onSubmit(event) {
    if (event.submitter && !event.submitter.matches('button[name="add"], input[name="add"]')) return;

    if (!this.#validate()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      return;
    }

    const hasFiles = Array.from(this.querySelectorAll('[data-pz-file]')).some((i) => !i.disabled && i.files?.length);
    const addon = !!this.querySelector('[data-pz-keychain]') && this.#twoSides();
    if (!hasFiles && !addon) return; // <s-product-form> handles the plain case.

    event.preventDefault();
    event.stopPropagation();
    this.#submitMultipart(event.submitter, addon);
  }

  async #submitMultipart(submitter, addon) {
    const host = this.closest('s-product-form');
    const errorBox = host?.querySelector('[data-add-to-cart-error]');
    if (host?.getAttribute('aria-busy') === 'true') return;
    host?.setAttribute('aria-busy', 'true');
    submitter?.setAttribute('aria-disabled', 'true');
    submitter?.classList.add('opacity-50', 'cursor-wait');
    if (errorBox) { errorBox.hidden = true; errorBox.textContent = ''; }

    try {
      const raw = new FormData(this.#form);
      const body = new FormData();
      for (const [name, value] of raw) {
        if (value instanceof File) continue;
        if (/^properties\[/.test(name) && !String(value).trim()) continue;
        body.append(name, value);
      }
      // Multi-photo upload keeps the previous theme's numbered keys.
      this.querySelectorAll('[data-pz-file]').forEach((input) => {
        if (input.disabled || !input.files?.length) return;
        const base = input.name.match(/^properties\[(.+?)( \d+)?\]$/);
        Array.from(input.files).forEach((file, i) => {
          const key = base && base[2] ? `properties[${base[1]} ${i + 1}]` : input.name;
          body.append(key, file, file.name);
        });
      });

      // Exact positioned photo on the keychain shape, so the cart and order show what the customer saw.
      if (this.#editor?.locked && this.#imageMode()) {
        const preview = await this.#editor.previewBlob();
        if (preview) body.append('properties[צד 1 - תצוגה מקדימה]', preview, 'keychain-preview.png');
      }

      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.description || '');

      if (addon) {
        const qty = Math.max(parseInt(body.get('quantity'), 10) || 1, 1);
        const addonId = this.querySelector('[data-pz-keychain]')?.dataset.addonVariant;
        const res2 = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(addonId), quantity: qty, properties: { 'תוספת עבור': 'חריטה בצד שני' } }] }),
        });
        if (!res2.ok) throw new Error((await res2.json().catch(() => ({})))?.description || '');
      }

      const cart = customElements.get('s-cart');
      if (cart?.refreshMountedCarts) await cart.refreshMountedCarts();
      if (window.Shopify?.actions?.openCart) await window.Shopify.actions.openCart();
      else window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
    } catch (error) {
      if (errorBox) { errorBox.textContent = error?.message || host?.dataset.errorMessage || ''; errorBox.hidden = false; }
    } finally {
      host?.removeAttribute('aria-busy');
      submitter?.removeAttribute('aria-disabled');
      submitter?.classList.remove('opacity-50', 'cursor-wait');
    }
  }
}

/**
 * Photo positioning on the keychain shape. Same output format as the previous theme:
 * "צורה=<shape>;זום=<z>;X=<x>;Y=<y>" in properties[צד 1 - מיקום תמונה].
 */
class ShapeEditor {
  constructor(box, host, opts) {
    this.box = box;
    this.host = host;
    this.fileInput = box.querySelector('[data-pz-editor-file]');
    this.meta = box.querySelector('[data-pz-editor-meta]');
    this.panel = box.querySelector('[data-pz-editor]');
    this.canvas = box.querySelector('[data-pz-editor-canvas]');
    this.zoom = box.querySelector('[data-pz-editor-zoom]');
    this.resetBtn = box.querySelector('[data-pz-editor-reset]');
    this.lockBtn = box.querySelector('[data-pz-editor-lock]');
    this.editBtn = box.querySelector('[data-pz-editor-edit]');
    this.lockedMsg = box.querySelector('[data-pz-editor-locked]');
    this.warn = box.querySelector('[data-pz-editor-warn]');
    this.shapeEl = box.querySelector('[data-pz-editor-shape]');
    this.ctx = this.canvas.getContext('2d');
    this.W = this.canvas.width;
    this.H = this.canvas.height;
    try { this.masks = JSON.parse(box.dataset.masks || '{}'); } catch (_) { this.masks = {}; }

    this.img = null;
    this.baseScale = 1;
    this.scale = 1;
    this.x = 0;
    this.y = 0;
    this.locked = false;
    this.mask = null;
    this.maskData = null;
    this.maskUrl = '';

    // Restore a previous session (variant change re-creates this editor).
    const saved = host.store.get(`${host.dataset.productId}::editor`);

    this.fileInput.addEventListener('change', () => this.#load(), opts);
    this.zoom.addEventListener('input', () => { if (!this.img || this.locked) return; this.scale = this.baseScale * parseFloat(this.zoom.value); this.#refresh(); }, opts);
    this.resetBtn.addEventListener('click', () => { if (!this.img || this.locked) return; this.#fit(); this.#refresh(); }, opts);
    this.lockBtn.addEventListener('click', () => { if (!this.img) return; if (!this.#coverage()) return this.#ui(); this.locked = true; this.#writeMeta(); this.#ui(); }, opts);
    this.editBtn.addEventListener('click', () => { this.locked = false; this.meta.value = ''; this.#ui(); }, opts);

    let drag = null;
    this.canvas.addEventListener('pointerdown', (e) => {
      if (!this.img || this.locked) return;
      drag = { px: e.clientX, py: e.clientY, ox: this.x, oy: this.y };
      this.canvas.setPointerCapture(e.pointerId);
    }, opts);
    this.canvas.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const k = this.W / this.canvas.getBoundingClientRect().width;
      this.x = drag.ox + (e.clientX - drag.px) * k;
      this.y = drag.oy + (e.clientY - drag.py) * k;
      this.#draw();
    }, opts);
    const end = () => { if (drag) { drag = null; this.#refresh(); } };
    this.canvas.addEventListener('pointerup', end, opts);
    this.canvas.addEventListener('pointercancel', end, opts);

    this.#loadMask().then(() => {
      if (this.fileInput.files?.length) this.#load(saved);
      else this.#draw();
    });
  }

  #shapeLabel() {
    const index = parseInt(this.box.dataset.shapeOption, 10) || 0;
    const value = index ? String(this.host.variantOptions()[index - 1] || '') : '';
    const keys = Object.keys(this.masks);
    const match = keys.find((k) => value.includes(k)) || keys.find((k) => k.split(' ').some((w) => value.includes(w)));
    return match || this.box.dataset.defaultShape || keys[0];
  }

  async #loadMask() {
    const shape = this.#shapeLabel();
    if (this.shapeEl) this.shapeEl.textContent = this.box.dataset.shapeLabel.replace('__S__', shape);
    const url = this.masks[shape];
    if (!url || url === this.maskUrl) return;
    this.maskUrl = url;
    const img = await new Promise((resolve, reject) => {
      const m = new Image();
      m.crossOrigin = 'anonymous';
      m.onload = () => resolve(m);
      m.onerror = reject;
      m.src = url;
    }).catch(() => null);
    if (!img) return;
    const c = document.createElement('canvas');
    c.width = this.W; c.height = this.H;
    const cx = c.getContext('2d');
    cx.drawImage(img, 0, 0, this.W, this.H);
    const data = cx.getImageData(0, 0, this.W, this.H);
    const d = data.data;
    this.maskData = new Uint8ClampedArray(this.W * this.H);
    for (let i = 0, p = 0; i < d.length; i += 4, p++) {
      const lum = (d[i] + d[i + 1] + d[i + 2]) / 3;
      d[i] = d[i + 1] = d[i + 2] = 255; d[i + 3] = lum;
      this.maskData[p] = lum;
    }
    cx.putImageData(data, 0, 0);
    this.mask = c;
  }

  #load(saved) {
    const file = this.fileInput.files?.[0];
    this.locked = false;
    this.meta.value = '';
    if (!file) { this.img = null; this.#ui(); this.#draw(); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        this.img = img;
        this.#fit();
        if (saved && saved.name === file.name) {
          this.scale = this.baseScale * saved.zoom; this.x = saved.x; this.y = saved.y;
          this.zoom.value = String(saved.zoom);
          this.locked = saved.locked && this.#coverage();
          if (this.locked) this.#writeMeta();
        }
        this.#refresh();
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  }

  #fit() {
    this.baseScale = Math.max(this.W / this.img.naturalWidth, this.H / this.img.naturalHeight);
    this.scale = this.baseScale;
    this.x = 0; this.y = 0;
    this.zoom.value = '1';
  }

  #composite() {
    const t = document.createElement('canvas');
    t.width = this.W; t.height = this.H;
    const tx = t.getContext('2d');
    tx.imageSmoothingQuality = 'high';
    const iw = this.img.naturalWidth * this.scale;
    const ih = this.img.naturalHeight * this.scale;
    tx.drawImage(this.img, (this.W - iw) / 2 + this.x, (this.H - ih) / 2 + this.y, iw, ih);
    if (this.mask) { tx.globalCompositeOperation = 'destination-in'; tx.drawImage(this.mask, 0, 0); }
    return t;
  }

  #draw() {
    const { ctx, W, H } = this;
    ctx.clearRect(0, 0, W, H);
    if (this.img) {
      // Faint full photo for context, then the engraved area at full strength.
      ctx.globalAlpha = 0.18;
      const iw = this.img.naturalWidth * this.scale;
      const ih = this.img.naturalHeight * this.scale;
      ctx.drawImage(this.img, (W - iw) / 2 + this.x, (H - ih) / 2 + this.y, iw, ih);
      ctx.globalAlpha = 1;
      ctx.drawImage(this.#composite(), 0, 0);
    }
    if (this.mask) {
      const o = document.createElement('canvas');
      o.width = W; o.height = H;
      const ox = o.getContext('2d');
      ox.fillStyle = '#181614';
      ox.fillRect(0, 0, W, H);
      ox.globalCompositeOperation = 'destination-in';
      for (const [dx, dy] of [[-3, 0], [3, 0], [0, -3], [0, 3], [-2, -2], [2, -2], [-2, 2], [2, 2]]) ox.drawImage(this.mask, dx, dy);
      ox.globalCompositeOperation = 'destination-out';
      ox.drawImage(this.mask, 0, 0);
      ctx.drawImage(o, 0, 0);
    }
  }

  #coverage() {
    if (!this.img || !this.maskData) { this.warn.hidden = true; return true; }
    const out = this.#composite().getContext('2d').getImageData(0, 0, this.W, this.H).data;
    for (let y = 0; y < this.H; y += 2) {
      for (let x = 0; x < this.W; x += 2) {
        const p = y * this.W + x;
        if (this.maskData[p] > 40 && out[p * 4 + 3] <= 10) { this.warn.hidden = false; return false; }
      }
    }
    this.warn.hidden = true;
    return true;
  }

  previewBlob() {
    if (!this.img) return Promise.resolve(null);
    const out = document.createElement('canvas');
    out.width = 600;
    out.height = 600;
    out.getContext('2d').drawImage(this.#composite(), 0, 0, 600, 600);
    return new Promise((resolve) => out.toBlob((b) => resolve(b), 'image/png'));
  }

  #writeMeta() {
    const z = this.baseScale ? this.scale / this.baseScale : 1;
    this.meta.value = `צורה=${this.#shapeLabel()};זום=${z.toFixed(3)};X=${this.x.toFixed(1)};Y=${this.y.toFixed(1)}`;
  }

  #ui() {
    const has = !!this.img;
    this.panel.hidden = !has;
    this.zoom.disabled = !has || this.locked;
    this.resetBtn.disabled = !has || this.locked;
    this.lockBtn.hidden = this.locked;
    this.editBtn.hidden = !this.locked;
    this.lockedMsg.hidden = !this.locked;
    this.canvas.classList.toggle('cursor-grab', has && !this.locked);
    const file = this.fileInput.files?.[0];
    this.host.store.set(`${this.host.dataset.productId}::editor`, file ? { name: file.name, zoom: parseFloat(this.zoom.value) || 1, x: this.x, y: this.y, locked: this.locked } : null);
  }

  #refresh() {
    this.#draw();
    this.#coverage();
    this.#ui();
  }
}

if (!customElements.get('s-personalization')) customElements.define('s-personalization', SPersonalization);
